/**
 * CoverInfoEditor Component
 * Shared cover page info editor used in both wizard edit step and report preview.
 *
 * variant="editor"  → Step 1: shows visibility toggles + reset button, dimmed hidden fields
 * variant="preview" → Step 2: fields hidden entirely when toggled off, preview styling
 */

import React from "react";
import PropTypes from "prop-types";
import moment from "moment";
import {
  FileText,
  Eye,
  EyeOff,
  Stethoscope,
  User,
  HeartPulse,
  RotateCcw,
} from "lucide-react";

const CoverInfoEditor = ({
  variant = "editor",
  doctorInfo,
  patientInfo,
  coverFieldVisibility,
  onEditCoverField,
  onToggleCoverVisibility,
  onResetCoverInfo,
  showHeader: showPreviewHeader,
  showGeneratedDate,
}) => {
  const cv = coverFieldVisibility || {
    doctor: { name: true, address: true, phone: true, email: true },
    patient: { name: true, dob: true, gender: true, age: true },
    medical: { background: true, lifestyle: true, allergies: true, treatments: true },
  };

  const isEditor = variant === "editor";

  // In editor mode, we dim hidden fields; in preview mode, we hide them entirely
  const isFieldVisible = (section, field) => cv[section]?.[field] !== false;

  const renderVisibilityToggle = (section, field) => {
    if (!isEditor || !onToggleCoverVisibility) return null;
    return (
      <button
        className="cover-visibility-toggle"
        onClick={() => onToggleCoverVisibility(section, field)}
        title={isFieldVisible(section, field) ? "Masquer" : "Afficher"}
      >
        {isFieldVisible(section, field) ? <Eye size={14} /> : <EyeOff size={14} />}
      </button>
    );
  };

  // CSS class helpers
  const fieldClass = (section, field, extra = "") => {
    const base = isEditor ? "cover-editor-field" : "cover-info-field";
    const hidden = isEditor && !isFieldVisible(section, field) ? `${base}--hidden` : "";
    return `${base} ${hidden} ${extra}`.trim();
  };

  const inputClass = isEditor ? "cover-editor-input" : "cover-info-input";

  // For preview mode, skip rendering hidden fields entirely
  const shouldRender = (section, field) => {
    if (isEditor) return true; // always render (dimmed when hidden)
    return isFieldVisible(section, field);
  };

  /* ─── Doctor Info Fields ─── */
  const renderDoctorFields = () => (
    <div className={isEditor ? "cover-editor-grid" : "cover-info-grid"}>
      {shouldRender("doctor", "name") && (
        <div className={fieldClass("doctor", "name")}>
          <div className={isEditor ? "cover-editor-field__header" : ""}>
            <label>Nom du Docteur</label>
            {renderVisibilityToggle("doctor", "name")}
          </div>
          <input
            type="text"
            className={inputClass}
            value={doctorInfo ? `Dr. ${doctorInfo.first_name || ""} ${doctorInfo.last_name || ""}` : ""}
            onChange={(e) => {
              if (!onEditCoverField) return;
              const val = e.target.value.replace(/^Dr\.\s*/, "");
              const parts = val.split(" ");
              onEditCoverField("doctor", "first_name", parts[0] || "");
              onEditCoverField("doctor", "last_name", parts.slice(1).join(" ") || "");
            }}
          />
        </div>
      )}
      {shouldRender("doctor", "address") && (
        <div className={fieldClass("doctor", "address")}>
          <div className={isEditor ? "cover-editor-field__header" : ""}>
            <label>Adresse</label>
            {renderVisibilityToggle("doctor", "address")}
          </div>
          <input
            type="text"
            className={inputClass}
            value={doctorInfo?.address || ""}
            onChange={(e) => onEditCoverField?.("doctor", "address", e.target.value)}
          />
        </div>
      )}
      {shouldRender("doctor", "phone") && (
        <div className={fieldClass("doctor", "phone")}>
          <div className={isEditor ? "cover-editor-field__header" : ""}>
            <label>Téléphone</label>
            {renderVisibilityToggle("doctor", "phone")}
          </div>
          <input
            type="text"
            className={inputClass}
            value={doctorInfo?.cell_phone || ""}
            onChange={(e) => onEditCoverField?.("doctor", "cell_phone", e.target.value)}
          />
        </div>
      )}
      {shouldRender("doctor", "email") && (
        <div className={fieldClass("doctor", "email")}>
          <div className={isEditor ? "cover-editor-field__header" : ""}>
            <label>Email</label>
            {renderVisibilityToggle("doctor", "email")}
          </div>
          <input
            type="text"
            className={inputClass}
            value={doctorInfo?.email || ""}
            onChange={(e) => onEditCoverField?.("doctor", "email", e.target.value)}
          />
        </div>
      )}
    </div>
  );

  /* ─── Patient Info Fields ─── */
  const renderPatientFields = () => (
    <div className={isEditor ? "cover-editor-grid" : "cover-info-grid"}>
      {shouldRender("patient", "name") && (
        <div className={fieldClass("patient", "name")}>
          <div className={isEditor ? "cover-editor-field__header" : ""}>
            <label>Nom du Patient</label>
            {renderVisibilityToggle("patient", "name")}
          </div>
          <input
            type="text"
            className={inputClass}
            value={patientInfo ? `${patientInfo.first_name || ""} ${patientInfo.last_name || ""}` : ""}
            onChange={(e) => {
              if (!onEditCoverField) return;
              const parts = e.target.value.split(" ");
              onEditCoverField("patient", "first_name", parts[0] || "");
              onEditCoverField("patient", "last_name", parts.slice(1).join(" ") || "");
            }}
          />
        </div>
      )}
      {shouldRender("patient", "dob") && (
        <div className={fieldClass("patient", "dob")}>
          <div className={isEditor ? "cover-editor-field__header" : ""}>
            <label>Date de naissance</label>
            {renderVisibilityToggle("patient", "dob")}
          </div>
          <input
            type="date"
            className={inputClass}
            value={patientInfo?.dob ? moment(patientInfo.dob).format("YYYY-MM-DD") : ""}
            onChange={(e) => onEditCoverField?.("patient", "dob", e.target.value)}
          />
        </div>
      )}
      {shouldRender("patient", "gender") && (
        <div className={fieldClass("patient", "gender")}>
          <div className={isEditor ? "cover-editor-field__header" : ""}>
            <label>Sexe</label>
            {renderVisibilityToggle("patient", "gender")}
          </div>
          <select
            className={inputClass}
            value={patientInfo?.gender || ""}
            onChange={(e) => onEditCoverField?.("patient", "gender", e.target.value)}
          >
            <option value="">—</option>
            <option value="male">Masculin</option>
            <option value="female">Féminin</option>
            <option value="Male">Masculin</option>
            <option value="Female">Féminin</option>
            <option value="Masculin">Masculin</option>
            <option value="Féminin">Féminin</option>
          </select>
        </div>
      )}
      {shouldRender("patient", "age") && (
        <div className={fieldClass("patient", "age")}>
          <div className={isEditor ? "cover-editor-field__header" : ""}>
            <label>Âge</label>
            {renderVisibilityToggle("patient", "age")}
          </div>
          <input
            type="text"
            className={inputClass}
            readOnly
            value={
              patientInfo?.dob
                ? moment().diff(moment(patientInfo.dob), "years")
                : (patientInfo?.age || "-")
            }
          />
        </div>
      )}
    </div>
  );

  /* ─── Medical Info Fields ─── */
  const renderMedicalFields = () => {
    const fullClass = isEditor ? "cover-editor-field--full" : "cover-info-field--full";
    return (
      <>
        {shouldRender("medical", "background") && (
          <div className={fieldClass("medical", "background", fullClass)}>
            <div className={isEditor ? "cover-editor-field__header" : ""}>
              <label>Antécédents médicaux</label>
              {renderVisibilityToggle("medical", "background")}
            </div>
            <input
              type="text"
              className={inputClass}
              readOnly
              value={
                patientInfo?.medical_background?.length > 0
                  ? patientInfo.medical_background
                      .map((item) => item.title || item.description || item.name)
                      .join(", ")
                  : isEditor ? "" : "-"
              }
              placeholder="Antécédents médicaux..."
            />
          </div>
        )}

        <div className={isEditor ? "cover-editor-grid" : "cover-info-grid"}>
          {shouldRender("medical", "lifestyle") && (
            <div className={fieldClass("medical", "lifestyle")}>
              <div className={isEditor ? "cover-editor-field__header" : ""}>
                <label>Mode de vie</label>
                {renderVisibilityToggle("medical", "lifestyle")}
              </div>
              <input
                type="text"
                className={inputClass}
                value={patientInfo?.lifestyle || patientInfo?.mode_de_vie || ""}
                onChange={(e) => onEditCoverField?.("patient", "lifestyle", e.target.value)}
                placeholder="Mode de vie..."
              />
            </div>
          )}
          {shouldRender("medical", "allergies") && (
            <div className={fieldClass("medical", "allergies")}>
              <div className={isEditor ? "cover-editor-field__header" : ""}>
                <label>Allergies</label>
                {renderVisibilityToggle("medical", "allergies")}
              </div>
              <input
                type="text"
                className={inputClass}
                value={patientInfo?.allergies || ""}
                onChange={(e) => onEditCoverField?.("patient", "allergies", e.target.value)}
                placeholder="Allergies..."
              />
            </div>
          )}
        </div>

        {shouldRender("medical", "treatments") && (
          <div className={fieldClass("medical", "treatments", fullClass)}>
            <div className={isEditor ? "cover-editor-field__header" : ""}>
              <label>Traitements en cours</label>
              {renderVisibilityToggle("medical", "treatments")}
            </div>
            <input
              type="text"
              className={inputClass}
              value={patientInfo?.current_treatments || patientInfo?.traitements_en_cours || ""}
              onChange={(e) => onEditCoverField?.("patient", "current_treatments", e.target.value)}
              placeholder="Traitements en cours..."
            />
          </div>
        )}
      </>
    );
  };

  /* ─── Render ─── */
  if (isEditor) {
    return (
      <div className="cover-info-editor">
        <div className="cover-info-editor__header">
          <h3>
            <FileText size={18} />
            Informations de la page de garde
          </h3>
          {onResetCoverInfo && (
            <button onClick={onResetCoverInfo} className="wizard-action-btn">
              <RotateCcw size={14} /> Réinitialiser
            </button>
          )}
        </div>

        {/* Doctor */}
        <div className="cover-info-editor__section">
          <h4 className="cover-editor-section__title">
            <Stethoscope size={16} />
            Informations du Médecin
          </h4>
          <div className="cover-editor-section__divider"></div>
          {renderDoctorFields()}
        </div>

        {/* Patient */}
        <div className="cover-info-editor__section">
          <h4 className="cover-editor-section__title">
            <User size={16} />
            Informations du Patient
          </h4>
          <div className="cover-editor-section__divider"></div>
          {renderPatientFields()}
        </div>

        {/* Medical */}
        <div className="cover-info-editor__section">
          <h4 className="cover-editor-section__title">
            <HeartPulse size={16} />
            Informations Médicales
          </h4>
          <div className="cover-editor-section__divider"></div>
          {renderMedicalFields()}
        </div>
      </div>
    );
  }

  // Preview variant
  return (
    <div className="report-cover-page">
      {showPreviewHeader && (
        <div className="cover-page__header">
          <div className="cover-page__logo">
            <Stethoscope size={28} />
            <span>{doctorInfo?.clinic_name || "Clinique Médicale"}</span>
          </div>
          {showGeneratedDate && (
            <p className="cover-page__date">
              Généré le {moment().format("DD MMMM YYYY, HH:mm")}
            </p>
          )}
        </div>
      )}

      <div className="cover-page__title">
        <h1>Rapport Médical</h1>
      </div>

      {/* Doctor */}
      <div className="cover-page__section">
        <h3 className="cover-section__heading">Informations du Médecin</h3>
        <div className="cover-section__divider"></div>
        {renderDoctorFields()}
      </div>

      {/* Patient */}
      <div className="cover-page__section">
        <h3 className="cover-section__heading">Informations du Patient</h3>
        <div className="cover-section__divider"></div>
        {renderPatientFields()}
      </div>

      {/* Medical */}
      <div className="cover-page__section">
        <h3 className="cover-section__heading">Informations Médicales</h3>
        <div className="cover-section__divider"></div>
        {renderMedicalFields()}
      </div>
    </div>
  );
};

CoverInfoEditor.propTypes = {
  variant: PropTypes.oneOf(["editor", "preview"]),
  doctorInfo: PropTypes.object,
  patientInfo: PropTypes.object,
  coverFieldVisibility: PropTypes.object,
  onEditCoverField: PropTypes.func,
  onToggleCoverVisibility: PropTypes.func,
  onResetCoverInfo: PropTypes.func,
  showHeader: PropTypes.bool,
  showGeneratedDate: PropTypes.bool,
};

CoverInfoEditor.defaultProps = {
  variant: "editor",
  showHeader: true,
  showGeneratedDate: true,
};

export default CoverInfoEditor;
